import React from 'react'

import '@fontsource/montserrat/300.css'
import '@fontsource/montserrat/400.css'
import '@fontsource/montserrat/400-italic.css'
import '@fontsource/montserrat/500.css'
import '@fontsource/rubik/600.css'
import '@fontsource/rubik/700.css'
import './src/styles/tailwind.css'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import { GatsbyBrowser } from 'gatsby'
// NOTE: AuthContextProvider is intentionally NOT imported here.
// It transitively pulls in `aws-amplify`, which is heavy (~150–250 KB gzipped).
// Anonymous visitors on public pages never need it, so it is mounted
// per-route only by pages that actually authenticate users (asiakastili,
// hallinta, lunasta, the claim flow, the shopify buy container, ...).
// The base `AuthContext` is still available everywhere via its default
// value, so `useContext(AuthContext)` calls in the header / public
// components keep working with `isLoggedIn: false` defaults.
import { MessageContextProvider } from './src/modules/messages/context/messageContextProvider'
import { CartContextProvider } from './src/modules/cart/context/cartContextProvider'
import { SlideOverProvider } from './src/components/slideOver/SlideOverContainer'
import { CookiePreferencesButton } from './src/components/cookiePreferencesButton/CookiePreferencesButton'
import { initAnalytics } from './src/modules/analytics/service/bootstrap'
import { trackPageView } from './src/modules/analytics/service/track'
import { trackFacebookPageView } from './src/modules/analytics/service/facebookPixel'

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
  initAnalytics()
}

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location }) => {
  trackPageView(location.pathname)
  trackFacebookPageView()
}

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
  element,
}) => {
  return (
    <MessageContextProvider>
      <CartContextProvider>{element}</CartContextProvider>
    </MessageContextProvider>
  )
}

// Nest SlideOverProvider INSIDE the providers added by
// `gatsby-plugin-react-i18next` so that the slide-over portal rendered by
// `SlideOverProvider` (e.g. the service contact form) has access to the
// `I18nextProvider` / `I18nextContext.Provider` context and translations
// work correctly. Because our `gatsby-browser.tsx` `wrapPageElement` is
// applied AFTER (outside of) plugin `wrapPageElement`s, we cannot simply
// wrap `element` directly — we need to clone-element our way inside the
// i18n providers and inject `SlideOverProvider` around the actual page
// children.
// See: https://andremonteiro.pt/gatsby-i18next-wrap-page-element
export const wrapPageElement: GatsbyBrowser['wrapPageElement'] = ({
  element,
}) => {
  return React.cloneElement(
    element, // I18nextProvider
    element.props,
    React.cloneElement(
      element.props.children, // I18nextContext.Provider
      element.props.children.props,
      <SlideOverProvider>
        {element.props.children.props.children}
        <CookiePreferencesButton />
      </SlideOverProvider>
    )
  )
}
